/**
 * The DOM attributes `AnimateNumber` writes and the portals client-error reporter reads back in its
 * hydration counter snapshot. Shared as constants — its own module so the client ENTRY bundle, which
 * builds the snapshot, does not pull the whole component in — because the two sides live in different
 * packages and a rename tied only by convention would silently blank the snapshot.
 */
export const COUNTER_VALUE_ATTR = 'data-value';
export const COUNTER_MODE_ATTR = 'data-counter-mode';

/**
 * The modes {@link COUNTER_MODE_ATTR} can carry, and what a snapshot line in each one is worth.
 *
 * `instant` and `entrance` come from `AnimateNumber`. `static` is for a figure that is NOT a counter at
 * all — a number rendered straight into the markup, formatted once, with no animation on either side.
 *
 * WHY `static` EARNS ITS PLACE (TEC-3804). The snapshot could only ever see nodes `AnimateNumber`
 * writes, and of its two modes only `instant` renders a live figure on the first pass — so `entrance`
 * lines can never show a real mismatch. A page whose counters are all `entrance` therefore produces a
 * snapshot that looks complete and proves nothing, which is exactly what TEC-3804 arrived with: a
 * confirmed `#418 args[]=text`, and every counter line reading `…=>0` because that is what both sides
 * are supposed to render.
 *
 * A `static` line has neither excuse. Both sides format the same number once, so if the attribute and
 * the text disagree, something genuinely diverged — and which half diverged says which:
 *
 * - attribute and text name the SAME number, formatted differently (`1.234` vs `1,234`) → the two
 *   renders used different locales, the TEC-3689 race.
 * - attribute and text name DIFFERENT numbers → the two renders had different data, i.e. the query
 *   behind the figure did not hydrate from the SSR payload (TEC-3758's persisted cache, or the lost
 *   query hydration the router-core patch describes).
 *
 * That is the whole reason to stamp a non-counter: it turns "some text differed" into a two-way
 * question with a decidable answer.
 */
export const COUNTER_MODE_INSTANT = 'instant';
export const COUNTER_MODE_ENTRANCE = 'entrance';
export const COUNTER_MODE_STATIC = 'static';
// The locale the render that produced the node formatted with. React does not patch attributes on
// hydration, so after a mismatch the DOM keeps the SERVER's value on hydrated nodes and the CLIENT's
// on client-rendered ones — which is exactly what lets the snapshot name the side that formatted
// with the wrong locale (the open question in the TEC-3696 family of #418 tickets).
export const COUNTER_LOCALE_ATTR = 'data-counter-locale';
